import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, finalize, map, of, throwError } from 'rxjs';
import { AutocompleteConfig, DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { CashFunding, CashFundingStatus } from '../../../models/cash-funding/cash-funding';
import { Business, BusinessEntityType } from '../../../models/business';
import { Currency } from '../../../models/bank-account/bank-account';
import { CashFundingProxyService } from '../../../proxies/cash-funding.proxy.service';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debouncedServerSearch } from '@card-software/nexus-utilities';

interface CashFundingFilters {
  funder?: Business | string | null;
  status?: number | string | null;
  fundingDateFrom?: Date | null;
  fundingDateTo?: Date | null;
}

/** Cash Funding dashboard — summary ("funded this week") + collapsible filters + the fundings table. */
@Component({
  selector: 'app-cash-fundings',
  templateUrl: './cash-fundings.component.html',
  styleUrl: './cash-fundings.component.scss',
})
export class CashFundingsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly service = inject(CashFundingProxyService);
  private readonly businessService = inject(BusinessProxyService);
  private readonly translate = inject(TranslateService);

  private readonly currencyLabels: Record<number, string> = { [Currency.USD]: 'USD', [Currency.HNL]: 'HNL', [Currency.EUR]: 'EUR' };

  readonly isLoading = signal(0);
  readonly fundings = signal<CashFunding[]>([]);
  readonly filtered = signal<CashFunding[]>([]);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'cash-funding-filters', config: this.filterConfig });

  // ── Summary (over all fundings, independent of filters) ──────────────────────
  readonly fundedThisWeek = computed(() => this.sumPostedBetween(this.startOfWeek(), this.endOfWeek()));
  readonly fundedThisMonth = computed(() => this.sumPostedBetween(this.startOfMonth(), this.endOfMonth()));
  readonly draftCount = computed(() => this.fundings().filter((f) => f.status === CashFundingStatus.Draft).length);
  readonly postedCount = computed(() => this.fundings().filter((f) => f.status === CashFundingStatus.Posted).length);

  readonly totalRecords = computed(() => this.filtered().length);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentPage = signal(1);
  readonly pageSize = signal(25);

  readonly paginatedFundings = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filtered().slice(start, start + this.pageSize());
  });

  private readonly funderQueries$ = new Subject<string>();

  private readonly funderAutocompleteConfig: AutocompleteConfig = {
    suggestions: [],
    optionLabel: 'name',
    optionValue: 'id',
    completeMethod: (event: AutoCompleteCompleteEvent) => this.funderQueries$.next(event.query),
    resolveById: (id: string) => this.resolveFunder(id),
    dropdown: true,
    forceSelection: true,
  };

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as CashFundingFilters));
  }

  ngOnInit(): void {
    // The panel owns the <p-autoComplete>, so there is no element for ServerSearchDirective: the
    // operator is wired by hand and this subscription is released with the component.
    this.funderQueries$
      .pipe(
        debouncedServerSearch((query) => this.searchFunders(query)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((suggestions) => (this.funderAutocompleteConfig.suggestions = suggestions));

    this.filterConfig.set(this.buildFilterConfig());
    this.load();
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private load(): void {
    this.startLoading();
    this.service
      .getAll()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const data = response.data ?? [];
          this.fundings.set(data);
          this.searchWithFilters(this.filterState.applied() as CashFundingFilters);
        },
        error: () => {
          this.fundings.set([]);
          this.filtered.set([]);
        },
      });
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'autocomplete',
          formControlName: 'funder',
          label: this.t('CashFunding_Funder', 'Funder'),
          placeholder: this.t('CashFunding_Funder_Search', 'Search funder'),
          autocompleteConfig: this.funderAutocompleteConfig,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          options: [
            { label: this.t('Draft', 'Draft'), value: CashFundingStatus.Draft },
            { label: this.t('Posted', 'Posted'), value: CashFundingStatus.Posted },
            { label: this.t('Cancelled', 'Cancelled'), value: CashFundingStatus.Cancelled },
          ],
          showClear: true,
          defaultVisible: true,
        },
        { type: 'date', formControlName: 'fundingDateFrom', label: this.t('CashFunding_DateFrom', 'Funded from'), defaultVisible: true },
        { type: 'date', formControlName: 'fundingDateTo', label: this.t('CashFunding_DateTo', 'Funded to'), defaultVisible: true },
      ],
    };
  }

  private resolveFunder(id: string): Observable<Business | null> {
    return this.businessService.getBusinessById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private searchFunders(query: string): Observable<Business[]> {
    return this.businessService
      .search({ name: query, code: query, businessTypes: [BusinessEntityType.InternalCompany] }, 1, 8, undefined, undefined, true)
      .pipe(map((response) => response.data?.items ?? []));
  }

  private searchWithFilters(filters: CashFundingFilters): void {
    const funder = filters.funder;
    const funderId = typeof funder === 'string' ? funder : (funder?.id ?? null);
    const status = filters.status === null || filters.status === undefined || filters.status === '' ? null : Number(filters.status);
    const from = filters.fundingDateFrom ? new Date(filters.fundingDateFrom).getTime() : null;
    const to = filters.fundingDateTo ? new Date(filters.fundingDateTo).getTime() : null;

    this.filtered.set(
      this.fundings().filter((f) => {
        if (funderId && f.funderReference !== funderId) return false;
        if (status !== null && f.status !== status) return false;
        const d = new Date(f.fundingDate).getTime();
        if (from !== null && d < from) return false;
        if (to !== null && d > to) return false;
        return true;
      }),
    );
    this.currentPage.set(1);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
  }

  // ── Summary helpers ─────────────────────────────────────────────────────────
  private sumPostedBetween(start: Date, end: Date): number {
    const s = start.getTime();
    const e = end.getTime();
    return this.fundings()
      .filter((f) => f.status === CashFundingStatus.Posted)
      .filter((f) => {
        const d = new Date(f.fundingDate).getTime();
        return d >= s && d <= e;
      })
      .reduce((sum, f) => sum + (f.amount ?? 0), 0);
  }

  private startOfWeek(): Date {
    const d = new Date();
    const day = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  private endOfWeek(): Date {
    const d = this.startOfWeek();
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
  }
  private startOfMonth(): Date {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
  }
  private endOfMonth(): Date {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  // ── Display ─────────────────────────────────────────────────────────────────
  currencyLabel(currency: number | null | undefined): string {
    return currency == null ? '' : (this.currencyLabels[currency] ?? '');
  }

  statusLabel(status: number): string {
    return CashFundingStatus[status] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case CashFundingStatus.Posted:
        return 'success';
      case CashFundingStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  newFunding(): void {
    this.router.navigate(['/cash-funding-details']);
  }

  view(row: CashFunding): void {
    if (row.id) this.router.navigate(['/cash-funding-details'], { queryParams: { id: row.id } });
  }

  trackById = (_: number, f: CashFunding) => f.id;
}
